import React, { useEffect, useState } from 'react';
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';
import { styled } from '@mui/material/styles';
import PaymentsAPI from 'api/PaymentsAPI';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';

interface IMeterCell {
  type: string;
  meterSubs: any;
  cusId: string;
}

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 10,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.grey[800],
    ...theme.applyStyles('dark', {
      backgroundColor: theme.palette.grey[800],
    }),
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: '#1a90ff',
    ...theme.applyStyles('dark', {
      backgroundColor: '#308fe8',
    }),
  },
}));

const MeterCells: React.FC<IMeterCell> = ({ type, meterSubs, cusId }) => {
  const [ peopleTotal, setPeopleTotal ] = useState({ isLoading: true, data: 0 });
  const [ productsTotal, setProductsTotal ] = useState({ isLoading: true, data: 0 });
  const [ formsTotal, setFormsTotal ] = useState({ isLoading: true, data: 0 });

  const getSummaries = async() => {
    meterSubs.data.forEach(async(sub: any) => {
      if (sub.metadata.moduleName === 'People') {
        const res = await PaymentsAPI.meterSummary({
          meterId: sub.plan.meter,
          cusId: cusId,
          startDate: sub.plan.created,
          endDate: Math.floor(Date.now() / 1000)
        });
        if (res?.success && res.result.data.length > 0) {
          setPeopleTotal({ isLoading: false, data: res.result.data[0].aggregated_value });
        }
        if (res?.success && res.result.data.length === 0) {
          setPeopleTotal({ isLoading: false, data: 0 });
        }
      }
      if (sub.metadata.moduleName === 'Products') {
        const res = await PaymentsAPI.meterSummary({
          meterId: sub.plan.meter,
          cusId: cusId,
          startDate: sub.plan.created,
          endDate: Math.floor(Date.now() / 1000)
        });
        if (res?.success && res.result.data.length > 0) {
          setProductsTotal({ isLoading: false, data: res.result.data[0].aggregated_value });
        }
        if (res?.success && res.result.data.length === 0) {
          setProductsTotal({ isLoading: false, data: 0 });
        }
      }
      if (sub.metadata.moduleName === 'Forms') {
        const res = await PaymentsAPI.meterSummary({
          meterId: sub.plan.meter,
          cusId: cusId,
          startDate: sub.plan.created,
          endDate: Math.floor(Date.now() / 1000)
        });
        if (res?.success && res.result.data.length > 0) {
          setFormsTotal({ isLoading: false, data: res.result.data[0].aggregated_value });
        }
        if (res?.success && res.result.data.length === 0) {
          setFormsTotal({ isLoading: false, data: 0 });
        }
      }
    });
  };

  useEffect(() => {
    getSummaries();
  }, []);

  return (
    <>
      {!peopleTotal.isLoading && (
        <div className='usage-meter-row-cell'>
          <div className='usage-meter-row-cell-usage-group'>
            {type === 'BITE Standard' && (
              <>
                <div className='usage-meter-row-cell-usage-text'>
                  {peopleTotal.data} of 100 searches
                </div>
                <div className='billing-seats-bar-container'>
                  <BorderLinearProgress
                    variant='determinate'
                    value={(peopleTotal.data / 100) * 100}
                  />
                </div>
              </>
            )}
            {type === 'Standard' && (
              <>
                <div className='usage-meter-row-cell-usage-text'>
                  {peopleTotal.data} of 100 searches
                </div>
                <div className='billing-seats-bar-container'>
                  <BorderLinearProgress
                    variant='determinate'
                    value={(peopleTotal.data / 100) * 100}
                  />
                </div>
              </>
            )}
            {type === 'BITE Pro' && (
              <>
                <div className='usage-meter-row-cell-usage-text'>
                  {peopleTotal.data} of 175 searches
                </div>
                <div className='billing-seats-bar-container'>
                  <BorderLinearProgress
                    variant='determinate'
                    value={(peopleTotal.data / 175) * 100}
                  />
                </div>
              </>
            )}
            {type === 'Pro' && (
              <>
                <div className='usage-meter-row-cell-usage-text'>
                  {peopleTotal.data} of 175 searches
                </div>
                <div className='billing-seats-bar-container'>
                  <BorderLinearProgress
                    variant='determinate'
                    value={(peopleTotal.data / 175) * 100}
                  />
                </div>
              </>
            )}
            {type === 'BITE Enterprise' && (
              <>
                <div className='usage-meter-row-cell-usage-text'>
                  {peopleTotal.data} of <AllInclusiveIcon style={{ fontSize: 'medium' }} /> searches
                </div>
                <div className='billing-seats-bar-container'>
                  <BorderLinearProgress
                    variant='determinate'
                    value={(100 / 100) * 100}
                  />
                </div>
              </>
            )}
            {type === 'Enterprise' && (
              <>
                <div className='usage-meter-row-cell-usage-text'>
                  {peopleTotal.data} of <AllInclusiveIcon style={{ fontSize: 'medium' }} /> searches
                </div>
                <div className='billing-seats-bar-container'>
                  <BorderLinearProgress
                    variant='determinate'
                    value={(100 / 100) * 100}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {!productsTotal.isLoading && (
        <div className='usage-meter-row-cell'>
          <div className='usage-meter-row-cell-usage-group'>
            {type === 'BITE Standard' && (
              <>
                <div className='usage-meter-row-cell-usage-text'>
                  {productsTotal.data} of 75 searches
                </div>
                <div className='billing-seats-bar-container'>
                  <BorderLinearProgress
                    variant='determinate'
                    value={(productsTotal.data / 75) * 100}
                  />
                </div>
              </>
            )}
            {type === 'Standard' && (
              <>
                <div className='usage-meter-row-cell-usage-text'>
                  {productsTotal.data} of 75 searches
                </div>
                <div className='billing-seats-bar-container'>
                  <BorderLinearProgress
                    variant='determinate'
                    value={(productsTotal.data / 75) * 100}
                  />
                </div>
              </>
            )}
            {type === 'BITE Pro' && (
              <>
                <div className='usage-meter-row-cell-usage-text'>
                  {productsTotal.data} of 125 searches
                </div>
                <div className='billing-seats-bar-container'>
                  <BorderLinearProgress
                    variant='determinate'
                    value={(productsTotal.data / 125) * 100}
                  />
                </div>
              </>
            )}
            {type === 'Pro' && (
              <>
                <div className='usage-meter-row-cell-usage-text'>
                  {productsTotal.data} of 125 searches
                </div>
                <div className='billing-seats-bar-container'>
                  <BorderLinearProgress
                    variant='determinate'
                    value={(productsTotal.data / 125) * 100}
                  />
                </div>
              </>
            )}
            {type === 'BITE Enterprise' && (
              <>
                <div className='usage-meter-row-cell-usage-text'>
                  {productsTotal.data} of <AllInclusiveIcon style={{ fontSize: 'medium' }} /> searches
                </div>
                <div className='billing-seats-bar-container'>
                  <BorderLinearProgress
                    variant='determinate'
                    value={(100 / 100) * 100}
                  />
                </div>
              </>
            )}
            {type === 'Enterprise' && (
              <>
                <div className='usage-meter-row-cell-usage-text'>
                  {productsTotal.data} of <AllInclusiveIcon style={{ fontSize: 'medium' }} /> searches
                </div>
                <div className='billing-seats-bar-container'>
                  <BorderLinearProgress
                    variant='determinate'
                    value={(100 / 100) * 100}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {!formsTotal.isLoading && (
        <div className='usage-meter-row-cell' style={{ border: 'none' }}>
          <div className='usage-meter-row-cell-usage-group'>
            {type === 'BITE Standard' && (
              <>
                <div className='usage-meter-row-cell-usage-text'>
                  {formsTotal.data} of 12 uploads
                </div>
                <div className='billing-seats-bar-container'>
                  <BorderLinearProgress
                    variant='determinate'
                    value={(formsTotal.data / 12) * 100}
                  />
                </div>
              </>
            )}
            {type === 'Standard' && (
              <>
                <div className='usage-meter-row-cell-usage-text'>
                  {formsTotal.data} of 12 uploads
                </div>
                <div className='billing-seats-bar-container'>
                  <BorderLinearProgress
                    variant='determinate'
                    value={(formsTotal.data / 12) * 100}
                  />
                </div>
              </>
            )}
            {type === 'BITE Pro' && (
              <>
                <div className='usage-meter-row-cell-usage-text'>
                  {formsTotal.data} of 30 uploads
                </div>
                <div className='billing-seats-bar-container'>
                  <BorderLinearProgress
                    variant='determinate'
                    value={(formsTotal.data / 30) * 100}
                  />
                </div>
              </>
            )}
            {type === 'Pro' && (
              <>
                <div className='usage-meter-row-cell-usage-text'>
                  {formsTotal.data} of 30 uploads
                </div>
                <div className='billing-seats-bar-container'>
                  <BorderLinearProgress
                    variant='determinate'
                    value={(formsTotal.data / 30) * 100}
                  />
                </div>
              </>
            )}
            {type === 'BITE Enterprise' && (
              <>
                <div className='usage-meter-row-cell-usage-text'>
                  {formsTotal.data} of 100 uploads
                </div>
                <div className='billing-seats-bar-container'>
                  <BorderLinearProgress
                    variant='determinate'
                    value={(formsTotal.data / 100) * 100}
                  />
                </div>
              </>
            )}
            {type === 'Enterprise' && (
              <>
                <div className='usage-meter-row-cell-usage-text'>
                  {formsTotal.data} of 100 uploads
                </div>
                <div className='billing-seats-bar-container'>
                  <BorderLinearProgress
                    variant='determinate'
                    value={(formsTotal.data / 100) * 100}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default MeterCells;
